import CandidatureAddByOrderIdRequest from "../../api/raily/candidature/add-by-order-id/candidature-add-by-order-id.request";
import {
  CandidatureAddByOrderIdNotAcceptableResponse,
  CandidatureAddByOrderIdNotAcceptableResponseErrorType,
} from "../../api/raily/candidature/add-by-order-id/candidature-add-by-order-id.response";
import CandidatureAddRequest from "../../api/raily/candidature/add/candidature-add.request";
import {
  CandidatureAddNotAcceptableResponse,
  CandidatureAddNotAcceptableResponseErrorType,
} from "../../api/raily/candidature/add/candidature-add.response";
import candidatureApiService from "../../api/raily/candidature/candidature-api.service";
import CandidaturePreviewRequest from "../../api/raily/candidature/preview/candidature-preview.request";
import {
  CandidaturePreviewNotAcceptableResponse,
  CandidaturePreviewNotAcceptableResponseErrorType,
  CandidaturePreviewResponseItem,
} from "../../api/raily/candidature/preview/candidature-preview.response";
import dataLoadErrorFactory from "../../utils/data-load-error/data-load-error.factory";
import handleErrorFactory from "../../utils/handle-error/handle-error.factory";
import HttpError from "../../utils/http/http.error";
import CandidatureAddByOrderIdError from "./add-by-order-id/candidature-add-by-order-id-error";
import driverPlanCandidatureAddByOrderIdErrorFactory from "./add-by-order-id/candidature-add-by-order-id-error.factory";
import CandidatureAddByOrderIdParams from "./add-by-order-id/candidature-add-by-order-id-params";
import driverPlanCandidatureAddByOrderIdRequestFactory from "./add-by-order-id/candidature-add-by-order-id-params.factory";
import CandidatureAddError from "./add/candidature-add-error";
import candidatureAddErrorFactory from "./add/candidature-add-error.factory";
import CandidatureAddParams from "./add/candidature-add-params";
import candidatureAddParamsFactory from "./add/candidature-add-params.factory";
import CandidatureDeleteOrderError from "./delete/candidature-delete-order-error";
import candidatureDeleteOrderErrorFactory from "./delete/candidature-delete-order-error.factory";
import CandidatureDeleteOrderParams from "./delete/candidature-delete-order-params";
import candidatureDeleteOrderParamsFactory from "./delete/candidature-delete-order-params.factory";
import CandidaturePreview from "./preview/candidature-preview";
import CandidaturePreviewError from "./preview/candidature-preview-error";
import candidaturePreviewErrorFactory from "./preview/candidature-preview-error.factory";
import CandidaturePreviewParams from "./preview/candidature-preview-params";
import candidaturePreviewParamsFactory from "./preview/candidature-preview-params.factory";
import candidaturePreviewFactory from "./preview/candidature-preview.factory";

const createErrorsFromNotAcceptableResponse = (
  response: CandidatureAddNotAcceptableResponse,
): CandidatureDeleteOrderError => {
  switch (response.data.errorType) {
    case CandidatureAddNotAcceptableResponseErrorType.NO_TAXI_DRIVER_ASSOCIATION:
      return candidatureDeleteOrderErrorFactory.createTaxiDriverAssociationNotExistsError();
    case CandidatureAddNotAcceptableResponseErrorType.DELETE_SOLVED_ORDER_TARGET_NOT_FOUND:
      return candidatureDeleteOrderErrorFactory.createOrderNotExistsError();
    case CandidatureAddNotAcceptableResponseErrorType.TOO_MANY_CHECKOUT_EVENTS:
      return candidatureDeleteOrderErrorFactory.createOrderInProgressError();
    case CandidatureAddNotAcceptableResponseErrorType.NO_VALID_TAXI_DRIVER_CONTRACT:
      return candidatureDeleteOrderErrorFactory.createTaxiDriverContractNotValidError();
    case CandidatureAddNotAcceptableResponseErrorType.NO_VALID_TAXI_RAILY_CONTRACT:
      return candidatureDeleteOrderErrorFactory.createTaxiRailyContractNotValidError();
    case CandidatureAddNotAcceptableResponseErrorType.DRIVER_HAS_CANDIDATURE:
      return candidatureDeleteOrderErrorFactory.createDriverHasCandidatureError();
    default:
      return dataLoadErrorFactory.createUnexpectedError();
  }
};

const createPreviewErrorsFromNotAcceptableResponse = (
  response: CandidaturePreviewNotAcceptableResponse,
): CandidaturePreviewError => {
  switch (response.data.errorType) {
    case CandidaturePreviewNotAcceptableResponseErrorType.PLAN_ENTRY_CANDIDATE_COLLISION:
      return candidaturePreviewErrorFactory.createPlanEntryCandidateCollisionError();
    case CandidaturePreviewNotAcceptableResponseErrorType.NO_TAXI_DRIVER_ASSOCIATION:
      return candidaturePreviewErrorFactory.createTaxiDriverAssociationNotExistsError();
    case CandidaturePreviewNotAcceptableResponseErrorType.DELETE_SOLVED_ORDER_TARGET_NOT_FOUND:
      return candidaturePreviewErrorFactory.createOrderNotExistsError();
    case CandidaturePreviewNotAcceptableResponseErrorType.TOO_MANY_CHECKOUT_EVENTS:
      return candidaturePreviewErrorFactory.createOrderInProgressError();
    case CandidaturePreviewNotAcceptableResponseErrorType.NO_VALID_TAXI_DRIVER_CONTRACT:
      return candidaturePreviewErrorFactory.createTaxiDriverContractNotValidError();
    case CandidaturePreviewNotAcceptableResponseErrorType.NO_VALID_TAXI_RAILY_CONTRACT:
      return candidaturePreviewErrorFactory.createTaxiRailyContractNotValidError();
    default:
      return dataLoadErrorFactory.createUnexpectedError();
  }
};

const createCandidatureAddErrorsFromNotAcceptableResponse = (
  response: CandidatureAddNotAcceptableResponse,
): any => {
  switch (response.data.errorType) {
    case CandidatureAddNotAcceptableResponseErrorType.PLAN_ENTRY_CANDIDATE_COLLISION:
      return candidatureAddErrorFactory.createPlanEntryCandidateCollisionError();
    case CandidatureAddNotAcceptableResponseErrorType.NO_TAXI_DRIVER_ASSOCIATION:
      return candidatureAddErrorFactory.createTaxiDriverAssociationNotExistsError();
    case CandidatureAddNotAcceptableResponseErrorType.DELETE_SOLVED_ORDER_TARGET_NOT_FOUND:
      return candidatureAddErrorFactory.createOrderNotExistsError();
    case CandidatureAddNotAcceptableResponseErrorType.TOO_MANY_CHECKOUT_EVENTS:
      return candidatureAddErrorFactory.createOrderInProgressError();
    case CandidatureAddNotAcceptableResponseErrorType.NO_VALID_TAXI_DRIVER_CONTRACT:
      return candidatureAddErrorFactory.createTaxiDriverContractNotValidError();
    case CandidatureAddNotAcceptableResponseErrorType.NO_VALID_TAXI_RAILY_CONTRACT:
      return candidatureAddErrorFactory.createTaxiRailyContractNotValidError();
    default:
      return dataLoadErrorFactory.createUnexpectedError();
  }
};

const deleteOrder = async (
  params: CandidatureDeleteOrderParams,
  abortSignal: AbortSignal,
): Promise<void> => {
  const request: CandidatureAddRequest =
    candidatureDeleteOrderParamsFactory.create(params);
  try {
    const response = await candidatureApiService().add(request, abortSignal);

    if (response.status === 406) {
      throw createErrorsFromNotAcceptableResponse(
        response as CandidatureAddNotAcceptableResponse,
      );
    }

    if (response.status !== 201) {
      throw dataLoadErrorFactory.createUnexpectedError();
    }
  } catch (_error) {
    const error = _error as HttpError | CandidatureDeleteOrderError;

    throw handleErrorFactory.handleCustomError(error);
  }
};

const getPreview = async (
  params: CandidaturePreviewParams,
  abortSignal: AbortSignal,
): Promise<CandidaturePreview> => {
  const request: CandidaturePreviewRequest =
    candidaturePreviewParamsFactory.create(params);
  try {
    const response = await candidatureApiService().getPreview(
      request,
      abortSignal,
    );

    if (response.status === 406) {
      throw createPreviewErrorsFromNotAcceptableResponse(
        response as CandidaturePreviewNotAcceptableResponse,
      );
    }

    if (response.status !== 200) {
      throw dataLoadErrorFactory.createUnexpectedError();
    }

    return candidaturePreviewFactory.create(
      response.data as CandidaturePreviewResponseItem,
    );
  } catch (_error) {
    const error = _error as HttpError | CandidaturePreviewError;
    throw handleErrorFactory.handleCustomError(error);
  }
};

const candidatureAdd = async (
  params: CandidatureAddParams,
  abortSignal: AbortSignal,
): Promise<void> => {
  const request: CandidatureAddRequest =
    candidatureAddParamsFactory.create(params);

  try {
    const response = await candidatureApiService().add(request, abortSignal);

    if (response.status === 406) {
      throw createCandidatureAddErrorsFromNotAcceptableResponse(
        response as CandidatureAddNotAcceptableResponse,
      );
    }

    if (response.status !== 201) {
      throw dataLoadErrorFactory.createUnexpectedError();
    }
  } catch (_error) {
    const error = _error as HttpError | CandidatureAddError;

    throw handleErrorFactory.handleCustomError(error);
  }
};

const createCandidatureAddByOrderIdErrorsFromNotAcceptableResponse = (
  response: CandidatureAddByOrderIdNotAcceptableResponse,
): any => {
  switch (response.data.errorType) {
    case CandidatureAddByOrderIdNotAcceptableResponseErrorType.DRIVER_HAS_CANDIDATURE:
      return driverPlanCandidatureAddByOrderIdErrorFactory.createDriverHasCandidatureError();
    case CandidatureAddByOrderIdNotAcceptableResponseErrorType.PLAN_ENTRY_CANDIDATE_COLLISION:
      return driverPlanCandidatureAddByOrderIdErrorFactory.createPlanEntryCandidateCollisionError();
    case CandidatureAddByOrderIdNotAcceptableResponseErrorType.NO_TAXI_DRIVER_ASSOCIATION:
      return driverPlanCandidatureAddByOrderIdErrorFactory.createTaxiDriverAssociationNotExistsError();
    case CandidatureAddByOrderIdNotAcceptableResponseErrorType.DELETE_SOLVED_ORDER_TARGET_NOT_FOUND:
      return driverPlanCandidatureAddByOrderIdErrorFactory.createOrderNotExistsError();
    case CandidatureAddByOrderIdNotAcceptableResponseErrorType.TOO_MANY_CHECKOUT_EVENTS:
      return driverPlanCandidatureAddByOrderIdErrorFactory.createOrderInProgressError();
    case CandidatureAddByOrderIdNotAcceptableResponseErrorType.NO_VALID_TAXI_DRIVER_CONTRACT:
      return driverPlanCandidatureAddByOrderIdErrorFactory.createTaxiDriverContractNotValidError();
    case CandidatureAddByOrderIdNotAcceptableResponseErrorType.NO_VALID_TAXI_RAILY_CONTRACT:
      return driverPlanCandidatureAddByOrderIdErrorFactory.createTaxiRailyContractNotValidError();
    case CandidatureAddByOrderIdNotAcceptableResponseErrorType.SOLVED_ORDER_CANDIDATE_START_TIME_EXPIRED_ERROR:
      return driverPlanCandidatureAddByOrderIdErrorFactory.createSolvedOrderCandidateStartTimeExpiredError();
    case CandidatureAddByOrderIdNotAcceptableResponseErrorType.PLAN_ENTRY_CANDIDATE_START_TIME_EXPIRED_ERROR:
      return driverPlanCandidatureAddByOrderIdErrorFactory.createPlanEntryCandidateStartTimeExpiredError();
    case CandidatureAddByOrderIdNotAcceptableResponseErrorType.CANDIDATE_FITS_WITH_NOT_ENOUGH_TIME:
      return driverPlanCandidatureAddByOrderIdErrorFactory.createPlanCandidateFitsWithNotEnoughTimeError(
        response.data,
      );
    case CandidatureAddByOrderIdNotAcceptableResponseErrorType.CANDIDATE_DOES_NOT_FIT:
      return driverPlanCandidatureAddByOrderIdErrorFactory.createPlanCandidateDoesNotFitError(
        response.data,
      );

    default:
      return dataLoadErrorFactory.createUnexpectedError();
  }
};

const candidatureAddByOrderId = async (
  params: CandidatureAddByOrderIdParams,
  abortSignal: AbortSignal,
): Promise<void> => {
  const request: CandidatureAddByOrderIdRequest =
    driverPlanCandidatureAddByOrderIdRequestFactory.create(params);

  try {
    const response = await candidatureApiService().addByOrderId(
      request,
      abortSignal,
    );

    if (response.status === 406) {
      throw createCandidatureAddByOrderIdErrorsFromNotAcceptableResponse(
        response as CandidatureAddByOrderIdNotAcceptableResponse,
      );
    }

    if (response.status !== 200) {
      throw dataLoadErrorFactory.createUnexpectedError();
    }
  } catch (_error) {
    const error = _error as HttpError | CandidatureAddByOrderIdError;

    throw handleErrorFactory.handleCustomError(error);
  }
};

const candidatureService = {
  deleteOrder,
  getPreview,
  candidatureAdd,
  candidatureAddByOrderId,
};

export default candidatureService;
